/// <amd-module name="VSS/Features/Monaco/MonacoEnvironment" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { registerLanguageExtensions } from "./Languages";

declare global {
    interface Window {
        MonacoEnvironment: any;
    }
}

interface IEditorWorkerFiles {
    css: string;
    default: string;
    html: string;
    json: string;
    ts: string;
}

// Update the versions provided here whenever the Monaco version is updated
// The files name may be found in the directory: npm/default/node_modules/vss-monaco-editor/dist

const DebugEditorWorkerFiles: IEditorWorkerFiles = {
    css: "debug/css.worker.c86148caedc94d602a6a.js",
    default: "debug/editor.worker.7e714a99b29b0379c4e6.js",
    html: "debug/html.worker.653b0f68772df1d5880a.js",
    json: "debug/json.worker.534387640ce41c5252ab.js",
    ts: "debug/ts.worker.42f1c4afdcba40b088a4.js"
};

const MinEditorWorkerFiles: IEditorWorkerFiles = {
    css: "min/css.worker.17ac4ac3a91f7217c392.js",
    default: "min/editor.worker.3a60c00f9ddfe99d7c6a.js",
    html: "min/html.worker.3fce5259dd5759ea6b78.js",
    json: "min/json.worker.6b999ac7a329266e6366.js",
    ts: "min/ts.worker.c3d813411da82905b10f.js"
};

/**
 * Monaco theme rules to apply for all themes
 */
const VsSharedThemeRules: monaco.editor.ITokenThemeRule[] = [
    { token: "debug-token", foreground: "9966FF" },
    { token: "error-token", foreground: "E92D3D" },
    { token: "info-token", foreground: "888888" },
    { token: "warn-token", foreground: "FF8C00" },
    { token: "command-token", foreground: "0078D4" },
    { token: "section-token", foreground: "1FAE3E" }
];

/**
 * VS theme rules plus common style overrides.
 * Ex: Override 'comments' to use #007200 for 4.97:1 contrast on green #d9ecd8 diff background.
 */
const VsDefaultThemeRules: monaco.editor.ITokenThemeRule[] = [
    { token: "comment", foreground: "007200" }, // Originally #008000
    { token: "number", foreground: "007200" }, // Originally #09885A
    ...VsSharedThemeRules
];

/**
 * VS Dark theme rules plus common style overrides plus console styles
 */
const VsDarkThemeRules: monaco.editor.ITokenThemeRule[] = [
    // Change background to pure black and foreground to pure white in dark theme
    { token: "", foreground: "FFFFFF", background: "000000" },
    { token: "comment", foreground: "13A10E" }, // Originally #008000
    { token: "number", foreground: "13A10E" }, // Originally #09885A
    ...VsSharedThemeRules
];

/**
 * High Contrast Black theme rules with added legacy console styles
 */
const HcBlackThemeRules: monaco.editor.ITokenThemeRule[] = [...VsSharedThemeRules];

/**
 * Service for configuring the monaco runtime environment
 */
export interface IMonacoEnvironmentService extends IVssService {
    /**
     * Ensures that the specified monaco theme is initialized and returns the name to pass to monaco.
     *
     * @param monacoTheme Preferred Monaco theme name
     */
    defineEditorTheme(monacoTheme?: monaco.editor.BuiltinTheme): string;

    /**
     * Setup the monaco environment so that the editor is ready to use. Must be called before creating an editor.
     * @param fileExtension extension of the file
     * @param fileUrl path of the file
     * @param actionDisposables array of disposables
     * @param delayedHighlighting callback for highlighting on hover
     */
    setupMonacoEnvironment(fileExtension?: string, fileUrl?: string, actionDisposables?: monaco.IDisposable[], delayedHighlighting?: (ranges: monaco.IRange[], theme: string) => void): void;
}

class MonacoEnvironmentService extends VssService implements IMonacoEnvironmentService {
    private definedThemes = new Set<string>();

    public setupMonacoEnvironment(fileExtension?: string, fileUrl?: string, disposables?: monaco.IDisposable[], delayedHighlighting?: (ranges: monaco.IRange[], theme: string) => void): void {
        if (!window.MonacoEnvironment) {
            window.MonacoEnvironment = {
                getWorkerUrl: (workerId: string, label: string) => {
                    const isDebug = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!.isDebugMode;
                    const filePaths = isDebug ? DebugEditorWorkerFiles : MinEditorWorkerFiles;

                    let filePath: string;
                    if (label === "json") {
                        filePath = filePaths.json;
                    } else if (label === "css" || label === "scss") {
                        filePath = filePaths.css;
                    } else if (label === "html") {
                        filePath = filePaths.html;
                    } else if (label === "typescript" || label === "javascript") {
                        filePath = filePaths.ts;
                    } else {
                        filePath = filePaths.default;
                    }

                    // Use the local static content URL for workers since HTML5 does not allow cross-domain web workers
                    // Additional options: https://github.com/Microsoft/monaco-editor/blob/master/docs/integrate-amd-cross.md
                    const localStaticRoot = (window as any).__extensionsLocalStaticRoot;
                    return localStaticRoot + "ms.vss-features/monaco-editor/modules/vss-monaco-editor/dist/" + filePath;
                }
            };
        }

        registerLanguageExtensions(this.pageContext, fileExtension, fileUrl, disposables, delayedHighlighting);
    }

    /**
     * Gets the Monaco theme information.  This can be based on 'vs' | 'vs-dark' | 'hc-black' with additional rules that extend/override the built-in themes
     */
    public defineEditorTheme(monacoTheme?: monaco.editor.BuiltinTheme): string {
        monacoTheme = monacoTheme ? monacoTheme : "vs";

        // If using Edge with a Windows High Contrast Theme, use "vs" instead of "hc-black" to match Windows HC styles.
        if (
            window.navigator.userAgent.toLowerCase().indexOf("edge") >= 0 &&
            window.matchMedia &&
            window.matchMedia("(-ms-high-contrast:active)").matches
        ) {
            monacoTheme = "vs";
        }

        const themeName = "tfs-extension-" + monacoTheme;
        if (!this.definedThemes.has(themeName)) {
            // Monaco uses theme rules to add inline styles for font color and weight, and also adds the monacoTheme as a class.
            let rules: monaco.editor.ITokenThemeRule[] = [];
            if (monacoTheme === "vs") {
                rules = VsDefaultThemeRules;
            } else if (monacoTheme === "vs-dark") {
                rules = VsDarkThemeRules;
            } else if (monacoTheme === "hc-black") {
                rules = HcBlackThemeRules;
            }

            const themeData = {
                base: monacoTheme,
                inherit: true,
                rules: rules,
                colors: {}
            };

            monaco.editor.defineTheme(themeName, themeData);
            this.definedThemes.add(themeName);
        }

        return themeName;
    }
}

Services.add("IMonacoEnvironmentService", { serviceFactory: MonacoEnvironmentService });
